import React, { useState } from 'react';
import { Bookmark, BookmarkPlus, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';

/**
 * Saved-search popover. Sits in the filter bar and lets dispatchers stash
 * their top filter combinations for one-click recall. Renders three sections:
 *
 *   1. The "Save current filters as…" row (only when current filter state
 *      is non-empty).
 *   2. The list of saved searches, newest first.
 *   3. A subtle hint about the per-dashboard cap (10).
 *
 * The component is dumb about state — the parent owns the items list, the
 * apply callback (re-hydrate filter UI), and the save/delete mutations.
 */
export const SavedSearchesPopover = ({
  items = [],
  loading = false,
  hasActiveFilters = false,
  onApply,
  onSave,
  onDelete,
}) => {
  const [open, setOpen] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [busy, setBusy] = useState(false);

  const trimmedName = draftName.trim();
  const canSave = hasActiveFilters && trimmedName.length > 0 && !busy;

  const handleSave = async () => {
    if (!canSave) return;
    setBusy(true);
    try {
      const ok = await onSave(trimmedName);
      if (ok) setDraftName('');
    } finally {
      setBusy(false);
    }
  };

  const handleApply = (search) => {
    onApply(search);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-slate-700 bg-slate-900 hover:bg-slate-800 gap-1.5 h-9"
          data-testid="saved-searches-trigger"
        >
          <Bookmark className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Saved</span>
          {items.length > 0 && (
            <Badge variant="outline" className="ml-1 h-4 px-1 text-[10px] border-slate-600 text-slate-300">
              {items.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-0 bg-slate-900 border-slate-700"
        data-testid="saved-searches-popover"
      >
        {/* Header */}
        <div className="px-3 py-2 border-b border-slate-800 flex items-center justify-between">
          <h4 className="text-sm font-medium text-white flex items-center gap-1.5">
            <Bookmark className="h-3.5 w-3.5 text-amber-400" />
            Saved Searches
          </h4>
          <span className="text-[10px] text-slate-500">{items.length} / 10</span>
        </div>

        {/* Save current filters */}
        <div className="px-3 py-3 border-b border-slate-800 space-y-2">
          {hasActiveFilters ? (
            <>
              <label className="text-[11px] uppercase tracking-wide text-slate-500">
                Save current filters as…
              </label>
              <div className="flex gap-1.5">
                <Input
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  placeholder="e.g. Trouble + LFD < 2d"
                  maxLength={60}
                  className="h-8 bg-slate-950 border-slate-700 text-sm"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
                  data-testid="saved-search-name-input"
                />
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={!canSave}
                  className="h-8 bg-amber-600 hover:bg-amber-500 text-slate-900 gap-1 px-2.5"
                  data-testid="saved-search-save-btn"
                >
                  <BookmarkPlus className="h-3.5 w-3.5" />
                  Save
                </Button>
              </div>
            </>
          ) : (
            <p className="text-xs text-slate-500 italic">
              Apply a filter or search first — then save it here for one-click reuse.
            </p>
          )}
        </div>

        {/* List */}
        <div className="max-h-64 overflow-y-auto" data-testid="saved-searches-list">
          {loading ? (
            <p className="px-3 py-4 text-xs text-slate-500 text-center">Loading…</p>
          ) : items.length === 0 ? (
            <p className="px-3 py-4 text-xs text-slate-500 text-center">No saved searches yet.</p>
          ) : (
            items.map((s) => (
              <div
                key={s.id}
                className="group flex items-center justify-between px-3 py-2 hover:bg-slate-800/60 border-b border-slate-800/60 last:border-b-0"
                data-testid={`saved-search-row-${s.id}`}
              >
                <button
                  type="button"
                  onClick={() => handleApply(s)}
                  className="flex-1 min-w-0 text-left"
                  data-testid={`saved-search-apply-${s.id}`}
                >
                  <p className="text-sm text-white truncate" title={s.name}>{s.name}</p>
                  <p className="text-[10px] text-slate-500 truncate">
                    {summarizeFilters(s.filters)}
                  </p>
                </button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => onDelete(s.id)}
                  title="Delete"
                  data-testid={`saved-search-delete-${s.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Builds a tiny one-line summary of the saved filters so the user can
// distinguish entries at a glance without expanding each one.
const summarizeFilters = (filters = {}) => {
  const parts = [];
  if (filters.searchTerm) parts.push(`"${filters.searchTerm}"`);
  if (filters.statusFilter) parts.push(`status: ${filters.statusFilter}`);
  if (filters.sslFilter) parts.push('ssl');
  if (filters.driverFilter) parts.push('driver');
  if (filters.customerFilter) parts.push('customer');
  if (filters.includeClosed) parts.push('+closed');
  if (Array.isArray(filters.hiddenStatuses) && filters.hiddenStatuses.length > 0) {
    parts.push(`hides ${filters.hiddenStatuses.length}`);
  }
  return parts.length > 0 ? parts.join(' · ') : 'no active filters';
};
